/**
 * Collection infinite scroll — watches a sentinel at the bottom of the
 * product grid and appends the next page of products when it enters the
 * viewport. Works alongside the Turbo-frame filter swap: when filters
 * change the frame replaces all content (including the sentinel), and the
 * turbo:frame-load listener re-wires the observer to the fresh sentinel.
 *
 * AI: Entirely written by AI
 * Date: 2026-06-15
 * Model: Claude Sonnet 4.6
 * Updated: 2026-06-15 (Claude Opus 4.8) - Reuse one IntersectionObserver instead of recreating it per page
 */

import { log } from './log.js';
import { parseFragment } from './dom.js';

const SENTINEL_SELECTOR = '[data-infinite-scroll-sentinel]';
const GRID_CONTENT_SELECTOR = '#collection-grid-content';
const GRID_ROW_SELECTOR = '[data-collection-product-grid]';
// Trigger load this many px before the sentinel reaches the viewport bottom.
const ROOT_MARGIN = '600px 0px';

let scrollObserver = null;
let abortController = null;

function getGridRow() {
  return document.querySelector(GRID_ROW_SELECTOR);
}

async function loadNextPage(nextUrl, sentinel) {
  log('[InfiniteScroll] loading → ' + nextUrl);
  sentinel.setAttribute('aria-busy', 'true');

  let html;
  try {
    const res = await fetch(nextUrl, { headers: { Accept: 'text/html' } });
    if (!res.ok) throw new Error('HTTP ' + res.status);
    html = await res.text();
  } catch (err) {
    log('[InfiniteScroll] fetch error: ' + err.message);
    sentinel.removeAttribute('aria-busy');
    // Re-observe so the user can retry by scrolling away and back.
    observeSentinel(sentinel);
    return;
  }

  appendPage(html, sentinel);
}

function appendPage(html, oldSentinel) {
  const newGridRow = parseFragment(html, GRID_ROW_SELECTOR);
  const currentGridRow = getGridRow();
  if (!newGridRow || !currentGridRow) {
    log('[InfiniteScroll] grid row not found in response — aborting append');
    return;
  }

  // Pull the next sentinel out of the fetched row before moving nodes.
  const newSentinel = newGridRow.querySelector(SENTINEL_SELECTOR);
  if (newSentinel) newSentinel.remove();

  // Swap out the old sentinel, then batch-append all fetched product cards.
  oldSentinel.remove();
  currentGridRow.append(...newGridRow.children);

  if (newSentinel) {
    currentGridRow.appendChild(newSentinel);
    observeSentinel(newSentinel);
    log('[InfiniteScroll] appended page; next → ' + newSentinel.dataset.nextUrl);
  } else {
    log('[InfiniteScroll] last page appended');
  }
}

// Fires when a watched sentinel reaches the prefetch zone. Reads the sentinel
// off the entry rather than a closure, so one observer instance serves every
// page (and every filter swap) without being recreated per append.
function onSentinelVisible(entries) {
  const entry = entries[0];
  if (!entry.isIntersecting) return;
  const sentinel = entry.target;
  const nextUrl = sentinel.dataset.nextUrl;
  if (!nextUrl) return;
  // Disconnect immediately so we don't fire twice while the fetch is
  // in-flight. appendPage or the error handler re-arms as needed.
  scrollObserver.disconnect();
  loadNextPage(nextUrl, sentinel);
}

function observeSentinel(sentinel) {
  if (!scrollObserver) {
    scrollObserver = new IntersectionObserver(onSentinelVisible, { rootMargin: ROOT_MARGIN });
  }
  // Watch only the current sentinel, dropping any previously-watched
  // (now-detached) one left over from a prior page or filter swap.
  scrollObserver.disconnect();
  scrollObserver.observe(sentinel);
}

function setup() {
  const sentinel = document.querySelector(SENTINEL_SELECTOR);
  if (!sentinel) return; // No next page — nothing to do.
  observeSentinel(sentinel);
}

function teardown() {
  if (scrollObserver) {
    scrollObserver.disconnect();
    scrollObserver = null;
  }
  abortController?.abort();
  abortController = null;
}

function init() {
  teardown();
  setup();

  // Re-run setup after filter swaps replace the grid frame content.
  const frame = document.querySelector(GRID_CONTENT_SELECTOR);
  if (frame) {
    abortController = new AbortController();
    frame.addEventListener('turbo:frame-load', setup, { signal: abortController.signal });
  }
}

document.addEventListener('ep:ready', init);
document.addEventListener('turbo:load', init);
document.addEventListener('ep:change', teardown);

log('[InfiniteScroll] registered');
